/**
 * Page transitions on mobile: docs/performance.md §۳.۲۰ (fade, 2026-09-30) and §۳.۲۲ (direction,
 * 2026-10-01). iOS-style push/pop, RTL: forward slides the new page in from the left, Back reverses
 * it; switching between bottom-nav tabs only crossfades. Direction is set on <html data-route-dir>.
 *
 * - Search → doctor profile with the navigation preview (first visit, tapped): the preview itself
 *   fades in over the search page (CSS on `[data-profile-preview]`), then the real profile replaces it.
 * - Every other client navigation to another page: a View Transition crossfades the old screen into
 *   the new one. It starts at Next's `beforeHistoryChange` — the new page's data is ready and React
 *   has not rendered it yet (measured: the old snapshot is taken before the new page's DOM exists) —
 *   so the old screen is never held while data loads; at most ROUTE_FADE_MAX_WAIT_MS while the new
 *   page renders.
 *
 * Mobile only; never with reduced motion, for shallow/query-only changes, or for iOS Back (Safari's
 * swipe-back already animates). Browsers without View Transitions switch as before. Visual only: no
 * events, no layout change.
 */

type RouterLike = {
  events: { on: (e: any, h: (...a: any[]) => void) => void; off: (e: any, h: (...a: any[]) => void) => void };
};

/** Longest the old screen may stay while the new page renders; past it the switch is instant. */
export const ROUTE_FADE_MAX_WAIT_MS = 500;

const pathOf = (url: string) => {
  let path: string;
  try {
    path = new URL(url, 'http://n').pathname;
  } catch {
    path = url.split(/[?#]/)[0];
  }
  try {
    path = decodeURI(path);
  } catch {}
  return path.replace(/\/+$/, '') || '/';
};

/** A navigation to another page (not a query/hash change on the same one) gets the fade. */
export function shouldRouteFade(fromPath: string, toUrl: string): boolean {
  return pathOf(fromPath) !== pathOf(toUrl);
}

const TAB_ROOTS = new Set([
  '/',
  '/apphome',
  '/patient',
  '/patient/appointments',
  '/_',
  '/_/chats',
  '/dashboard/appointments',
  '/dashboard/apps/drapp/appointments',
]);
/** A bottom-nav tab root (search: `/s` or `/s/<city>` only; a specialty search is a drill-in). */
export const isTabRoot = (url: string) => {
  const path = pathOf(url);
  return TAB_ROOTS.has(path) || /^\/s(\/[^/]+)?$/.test(path);
};

export type RouteDir = 'forward' | 'back' | 'tab';
export function routeDirection(fromPath: string, toUrl: string, back: boolean): RouteDir {
  if (isTabRoot(fromPath) && isTabRoot(toUrl)) return 'tab';
  return back ? 'back' : 'forward';
}

// History position per Next history entry key, so Back/Forward buttons and gestures get a direction.
const IDX_KEY = 'p24_route_idx';
const readIdx = (): Record<string, number> => {
  try {
    return JSON.parse(sessionStorage.getItem(IDX_KEY) || '{}');
  } catch {
    return {};
  }
};
const stateKey = () => (history.state as any)?.key as string | undefined;

let previewShown = false;
/** ProfileNavigationPreview: this navigation shows the preview, which fades in on its own. */
export const markNavigationPreviewShown = () => {
  previewShown = true;
};

const canAnimate = () =>
  typeof document !== 'undefined' &&
  typeof (document as any).startViewTransition === 'function' &&
  !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches &&
  !window.matchMedia?.('(min-width: 1024px)').matches;

const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

export function startRouteFade(router: RouterLike): () => void {
  let finish: (() => void) | null = null;
  let popState = false;
  // Whether the navigation in progress came from Back/Forward (set at routeChangeStart).
  let pendingFromPop = false;
  // The page on screen. Not location.pathname: on Back the address already shows the destination.
  let currentPath = location.pathname;
  let idx = readIdx();
  let currentKey = stateKey();
  if (currentKey && !(currentKey in idx)) idx[currentKey] = 0;
  // Back/Forward: true when the popped entry is behind the current one.
  let popBack = true;
  let pendingBack = false;

  const onPopState = () => {
    popState = true;
    const to = stateKey();
    popBack = !(to && currentKey && to in idx && currentKey in idx && idx[to] > idx[currentKey]);
  };
  const done = () => {
    finish?.();
    finish = null;
  };
  const clearDir = () => {
    delete document.documentElement.dataset.routeDir;
  };

  const onStart = () => {
    pendingFromPop = popState;
    pendingBack = popState && popBack;
    popState = false;
    previewShown = false;
    done();
  };

  const onBeforeHistoryChange = (as: string, { shallow }: { shallow?: boolean } = {}) => {
    if (shallow || !shouldRouteFade(currentPath, as) || !canAnimate()) return;
    if (pendingFromPop && isIOS()) return;
    if (previewShown) return; // search → profile: the preview already faded in
    let resolve!: () => void;
    const changed = new Promise<void>(r => (resolve = r));
    const timer = setTimeout(() => resolve(), ROUTE_FADE_MAX_WAIT_MS);
    finish = () => {
      clearTimeout(timer);
      resolve();
    };
    document.documentElement.dataset.routeDir = routeDirection(currentPath, as, pendingBack);
    try {
      const vt = (document as any).startViewTransition(() => changed);
      vt?.finished?.then(clearDir, clearDir);
    } catch {
      clearDir();
      done();
    }
  };

  const onComplete = () => {
    currentPath = location.pathname;
    const key = stateKey();
    if (key && !(key in idx)) {
      idx[key] = currentKey && currentKey in idx ? idx[currentKey] + 1 : 0;
      try {
        sessionStorage.setItem(IDX_KEY, JSON.stringify(idx));
      } catch {}
    }
    currentKey = key;
    done();
  };

  // Capture: before Next's own popstate listener, which starts the route change.
  window.addEventListener('popstate', onPopState, { capture: true });
  router.events.on('routeChangeStart', onStart);
  router.events.on('beforeHistoryChange', onBeforeHistoryChange);
  // routeChangeComplete runs in the commit's microtasks (after the scroll restore): the new screen.
  router.events.on('routeChangeComplete', onComplete);
  router.events.on('routeChangeError', done);
  return () => {
    done();
    window.removeEventListener('popstate', onPopState, { capture: true });
    router.events.off('routeChangeStart', onStart);
    router.events.off('beforeHistoryChange', onBeforeHistoryChange);
    router.events.off('routeChangeComplete', onComplete);
    router.events.off('routeChangeError', done);
  };
}
